import { SITE_HTML } from '@app/formats'
import type { Said as SaidText } from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { placeLine, townLine } from '../lib/address-place'
import { boardIsLong, matchesBoardSearch } from '../lib/dedication-board'
import type { OrgListItem } from '../lib/org'
import { orgHref } from '../lib/public-router'
import { useTRPC } from '../lib/trpc'
import { HEBREW, STRONG } from '../ui/formats/reading-direction'
import { directionsHref, GIVE_BAND, telHref } from '../ui/PublicShell'
import { RichTextView } from '../ui/RichTextView'
import { Said } from '../ui/Said'
import { SearchField } from '../ui/SearchField'
import { GivingCards } from './GivingCards'
import { OtherWaysToGive } from './OtherWaysToGive'
import './shul-home.css'

/*
 * The bands an org's front door is built from whatever leads it: its name,
 * the ways to give, and how to reach it. A shul's front door puts what is next
 * at the shul above them; a site that leads with giving puts nothing there.
 */

/**
 * The page's heading as the org's own name — where nothing leads on a shul's
 * front door, and the whole of the heading on a site that leads with giving.
 * The page still says whose it is, and the ways in below it stand.
 */
export function NamedHeading({ name }: { name: SaidText }) {
  return (
    <section className="home__hero" aria-labelledby="home-hero">
      <div className="home__measure">
        {name.en ? (
          <h1 id="home-hero" className="home__headline">
            <Said said={name} />
          </h1>
        ) : (
          <h1
            id="home-hero"
            className="home__headline home__headline--he"
            lang="he"
            dir="rtl"
          >
            {name.he}
          </h1>
        )}
      </div>
    </section>
  )
}

/**
 * The ways to give, as the donate page's own top level has them: a donation,
 * membership where the org offers any, then each campaign collecting — those
 * with a closing date first, soonest first, as the server orders them.
 */
export function Give({ orgId }: { orgId: string }) {
  const trpc = useTRPC()
  const campaigns = useQuery(
    trpc.donation.campaigns.queryOptions({ shortId: orgId }),
  )
  const menu = useQuery(trpc.donation.menu.queryOptions({ shortId: orgId }))
  const tiers = useQuery(trpc.donation.tiers.queryOptions({ shortId: orgId }))
  const org = useQuery(trpc.donation.org.queryOptions({ shortId: orgId }))
  const sections = useQuery(
    trpc.site.publicSections.queryOptions({ shortId: orgId }),
  )
  const band = useRef<HTMLElement>(null)
  const [query, setQuery] = useState('')
  const ready = !(
    campaigns.isLoading ||
    menu.isLoading ||
    tiers.isLoading ||
    org.isLoading ||
    sections.isLoading
  )

  /* The site's Give opens the home page at this band, which is drawn only once
     its reads have answered — after the browser has looked for the fragment
     and found nothing. So the band brings itself into view when it arrives. */
  useEffect(() => {
    if (ready && window.location.hash === `#${GIVE_BAND}`)
      band.current?.scrollIntoView()
  }, [ready])

  /* The rows are drawn once all three have answered, and once the org's own
     sections above them have: anything arriving after the rest pushes the
     list down under a reader already aiming at a row. */
  if (!ready) return null

  // The org's own menu, named — never a sentence about what it offers.
  const offered = (menu.data ?? [])
    .filter((i) => !i.catchAll)
    .map((i) => i.label)
  const general = [
    {
      key: 'donate',
      name: 'Donation',
      line: offered.length ? offered.join(' · ') : null,
      act: 'Give',
      href: orgHref(orgId, 'donate'),
    },
    ...(tiers.data?.tiers.length
      ? [
          {
            key: 'membership',
            name: 'Membership',
            line: 'Join, or renew your membership.',
            act: 'Join or renew',
            href: orgHref(orgId, 'donate'),
          },
        ]
      : []),
  ]
  const funds = (campaigns.data ?? []).map((c) => ({
    key: c.shortId,
    name: c.label,
    line: c.tagline,
    appeal: c.appeal ?? null,
    goalCents: c.goalCents ?? null,
    pledgedCents: c.pledgedCents ?? null,
    act: 'Give',
    href: orgHref(orgId, 'donate', c.shortId),
  }))
  const other = org.data?.otherWaysToGive ?? []
  const ways = [
    ...general.map((g) => ({
      ...g,
      appeal: null,
      goalCents: null,
      pledgedCents: null,
    })),
    ...funds,
  ]
  // A style and a layout are for funds; the general ways alone stay rows beside.
  const look = funds.length > 0 ? (org.data?.givingLook ?? 'list') : 'list'
  const layout =
    funds.length > 0 ? (org.data?.givingLayout ?? 'beside') : 'beside'

  /* A long list is found rather than read, as a long dedication board is: the
     field narrows it by a way's name and its line, whatever the look. */
  const long = boardIsLong(ways.length)
  const shown = long
    ? ways.filter(
        (w) =>
          matchesBoardSearch(w.name, query) ||
          matchesBoardSearch(w.line ?? '', query),
      )
    : ways
  const search = long ? (
    <div className="home__give-search">
      <SearchField
        value={query}
        onChange={setQuery}
        placeholder="Search the ways to give"
        ariaLabel="Search the ways to give"
      />
      {shown.length === 0 ? (
        <p className="home__give-none" role="status">
          Nothing here matches “{query.trim()}”.
        </p>
      ) : null}
    </div>
  ) : null

  // Every way to give in one style, the general ones first.
  const list =
    look === 'cards' ? (
      <GivingCards cards={shown} />
    ) : (
      <WayRows ways={shown} compact={look === 'compact'} />
    )

  /* Across the band: the heading over every way to give, and the other ways
     along its foot. */
  if (layout === 'across')
    return (
      <section
        ref={band}
        id={GIVE_BAND}
        className="home__band home__give"
        aria-labelledby="home-give-title"
      >
        <div className="home__measure home__give-wide">
          <h2 id="home-give-title" className="home__h2">
            Give
          </h2>
          {search}
          {list}
          <OtherWaysToGive items={other} />
        </div>
      </section>
    )

  return (
    <section
      ref={band}
      id={GIVE_BAND}
      className="home__band home__give"
      aria-labelledby="home-give-title"
    >
      <div className="home__measure home__split">
        <div className="home__give-head">
          <h2 id="home-give-title" className="home__h2">
            Give
          </h2>
          <OtherWaysToGive items={other} />
        </div>
        <div className="home__give-rows">
          {search}
          {list}
        </div>
      </div>
    </section>
  )
}

/**
 * The ways to give as rows, each the link to where that gift is given. Compact
 * sets each on one line, its own line clipped beside its name, in as many
 * columns as the measure holds — for a fund running dozens of cases.
 */
function WayRows({
  ways,
  compact = false,
}: {
  ways: {
    key: string
    name: string
    line: string | null
    act: string
    href: string
  }[]
  compact?: boolean
}) {
  // Nothing found draws no list, so the search's message stands alone.
  if (ways.length === 0) return null
  return (
    <ul className="home__ways" data-compact={compact ? '' : undefined}>
      {ways.map((r) => (
        <li key={r.key}>
          <a className="home__way" href={r.href}>
            <span className="home__way-text">
              <span className="home__way-name">{r.name}</span>
              {r.line ? <span className="home__way-line">{r.line}</span> : null}
            </span>
            <span className="home__way-act" aria-hidden>
              {r.act}
            </span>
          </a>
        </li>
      ))}
    </ul>
  )
}

/**
 * The org's own sections on one side of the ways to give, in the order it put
 * them: each a band like the others, headed by its title, its body set by this
 * page's own stylesheet.
 */
export function Sections({
  orgId,
  afterGiving,
}: {
  orgId: string
  afterGiving: boolean
}) {
  const trpc = useTRPC()
  const sections = useQuery(
    trpc.site.publicSections.queryOptions({ shortId: orgId }),
  )
  const here = (sections.data ?? []).filter(
    (s) => s.afterGiving === afterGiving,
  )
  return here.map((s) => (
    <section
      key={s.id}
      className="home__band"
      aria-labelledby={`home-section-${s.id}`}
    >
      <div className="home__measure home__split">
        {HEBREW.test(STRONG.exec(s.title)?.[0] ?? '') ? (
          <h2
            id={`home-section-${s.id}`}
            className="home__h2 home__h2--he"
            lang="he"
            dir="rtl"
          >
            {s.title}
          </h2>
        ) : (
          <h2 id={`home-section-${s.id}`} className="home__h2">
            {s.title}
          </h2>
        )}
        <div className="home__words">
          <RichTextView html={s.body} allow={SITE_HTML} />
        </div>
      </div>
    </section>
  ))
}

/**
 * Where the org is and how to reach it. Headed by what the band is for, as
 * Give is: the masthead already names the org, with a shul's rav's line
 * beneath the name while the page is at its top, or in the menu on a phone.
 */
export function Visit({ org }: { org: OrgListItem }) {
  const postal = placeLine(org)
  const street = org.addressLine?.trim() ?? ''
  const place = townLine(org)
  const phone = org.phone?.trim() ?? ''
  const email = org.email?.trim() ?? ''

  return (
    <section className="home__band home__visit" aria-labelledby="home-visit">
      <div className="home__measure home__split">
        <div className="home__visit-head">
          <h2 id="home-visit" className="home__h2">
            Contact us
          </h2>
        </div>
        <div className="home__visit-body">
          {postal ? (
            <address className="home__address">
              {street ? <span>{street}</span> : null}
              {place ? <span>{place}</span> : null}
              <a className="home__textlink" href={directionsHref(postal)}>
                Get directions
              </a>
            </address>
          ) : null}
          <div className="home__reach">
            {phone ? (
              <a className="home__phone" href={`tel:${telHref(phone)}`}>
                {phone}
              </a>
            ) : null}
            {email ? <a href={`mailto:${email}`}>{email}</a> : null}
            <a
              className="home__textlink"
              href={orgHref(org.shortId, 'contact')}
            >
              Send us a message
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
